import { Tabs as TabsPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

const Segments = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Root
    ref={ref}
    className={cn('relative', className)}
    {...props}
  />
));
Segments.displayName = TabsPrimitive.Root.displayName;

const SegmentsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const syncActiveSegment = React.useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const activeTab = list.querySelector(
      '[data-state="active"]'
    ) as HTMLElement | null;

    if (!activeTab) return;

    const rect = activeTab.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();

    list.style.setProperty('--tab-width', `${rect.width}px`);
    list.style.setProperty('--tab-left', `${rect.left - listRect.left}px`);
  }, []);

  React.useLayoutEffect(() => {
    syncActiveSegment();
  });

  return (
    <TabsPrimitive.List
      ref={(node) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
        listRef.current = node;
      }}
      className={cn(
        'relative',
        'border border-border',
        'rounded-lg',
        'h-10',
        'inline-flex items-center justify-center',
        'text-foreground-muted',
        'overflow-hidden',
        'before:absolute before:bottom-0 before:h-full before:rounded-[6px] before:bg-surface-active',
        'before:transition-none hover:before:transition-all before:duration-300 before:ease-in-out',
        'before:w-[var(--tab-width,0px)] before:left-[var(--tab-left,0px)] before:-translate-x-[1px]',
        className
      )}
      {...props}
    />
  );
});
SegmentsList.displayName = TabsPrimitive.List.displayName;

const SegmentsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      {...props}
      className={cn(
        'leading-none',
        'inline-flex items-center justify-center',
        'whitespace-nowrap',
        'px-3',
        'text-sm font-medium',
        'focus-ring-inset',
        "[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        'disabled:pointer-events-none disabled:opacity-50',
        'relative',
        'transition-colors',
        'rounded-md',
        'data-[state=active]:text-foreground hover:text-foreground-subtle',
        'group',
        className
      )}
      onMouseDown={(e) => {
        const target = e.currentTarget;
        const rect = target.getBoundingClientRect();
        const list = target.closest('[role="tablist"]') as HTMLElement | null;

        if (list) {
          list.style.setProperty('--tab-width', `${rect.width}px`);
          list.style.setProperty(
            '--tab-left',
            `${rect.left - list.getBoundingClientRect().left}px`
          );
        }

        if (props.onMouseDown) {
          props.onMouseDown(e);
        }
      }}
    />
  );
});
SegmentsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const SegmentsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('mt-2 focus-visible:outline-none', className)}
    {...props}
  />
));
SegmentsContent.displayName = TabsPrimitive.Content.displayName;

export const SegmentedControl = {
  Root: Segments,
  Content: SegmentsContent,
  List: SegmentsList,
  Trigger: SegmentsTrigger,
};
